import { useCallback, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SparkleIcon, DropArrowIcon } from '../components/Icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { getMessages } from '../services/storage';

export function Home() {
  const navigate = useNavigate();
  const { isLoggedIn, username } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showModeModal, setShowModeModal] = useState(false);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setShowModeModal(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const [multiFileWarning, setMultiFileWarning] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 1) {
        setMultiFileWarning(true);
        setTimeout(() => setMultiFileWarning(false), 5000);
      }
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleModeSelect = (mode: 'send' | 'store' | 'quick') => {
    setShowModeModal(false);
    // Navigate to upload with mode and pass selected file via state
    navigate(`/upload?mode=${mode}`, { state: { file: selectedFile } });
  };

  // Message counts for dashboard
  const stats = useMemo(() => {
    if (!username) return { received: 0, sent: 0, stored: 0 };
    return {
      received: getMessages(username, 'received').length,
      sent: getMessages(username, 'sent').length,
      stored: getMessages(username, 'stored').length,
    };
  }, [username]);

  // Logged-in dashboard
  if (isLoggedIn && username) {
    return (
      <div
        className="dropzone-wrapper"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Layout theme="coral">
          <div className="content-centered">
            <div className="dashboard">
              <div className="dashboard-header">
                <h1>Welcome back, {username}</h1>
                <p>Your decentralized file exchange</p>
              </div>

              <div className="dashboard-stats">
                <div className="dashboard-stat">
                  <div className="dashboard-stat-value">{stats.received}</div>
                  <div className="dashboard-stat-label">Received</div>
                </div>
                <div className="dashboard-stat">
                  <div className="dashboard-stat-value">{stats.sent}</div>
                  <div className="dashboard-stat-label">Sent</div>
                </div>
                <div className="dashboard-stat">
                  <div className="dashboard-stat-value">{stats.stored}</div>
                  <div className="dashboard-stat-label">Stored</div>
                </div>
              </div>

              <div className="dashboard-actions">
                <button className="dashboard-action" onClick={() => fileInputRef.current?.click()}>
                  <span className="dashboard-action-icon">
                    <SparkleIcon />
                  </span>
                  <div className="dashboard-action-text">
                    <div className="dashboard-action-title">Send a File</div>
                    <div className="dashboard-action-desc">Encrypted transfer to another user</div>
                  </div>
                </button>

                <button className="dashboard-action" onClick={() => navigate('/account/received')}>
                  <span className="dashboard-action-icon">
                    <DropArrowIcon />
                  </span>
                  <div className="dashboard-action-text">
                    <div className="dashboard-action-title">My Inbox</div>
                    <div className="dashboard-action-desc">{stats.received} file{stats.received !== 1 ? 's' : ''} received</div>
                  </div>
                </button>

                <button className="dashboard-action" onClick={() => navigate('/settings')}>
                  <span className="dashboard-action-icon" style={{ fontSize: 20 }}>&#9881;</span>
                  <div className="dashboard-action-text">
                    <div className="dashboard-action-title">Settings</div>
                    <div className="dashboard-action-desc">Account, stamps, network config</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </Layout>

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        {isDragging && (
          <div className="drop-indicator">Drop your file here</div>
        )}

        {/* Mode Selection Modal */}
        <Modal
          isOpen={showModeModal}
          onClose={() => setShowModeModal(false)}
          title={`What would you like to do with ${selectedFile?.name}?`}
        >
          <button
            className="modal-option"
            onClick={() => handleModeSelect('quick')}
          >
            <div className="modal-option-title">Quick Share</div>
            <div className="modal-option-desc">Unencrypted, no login needed</div>
          </button>
          <button
            className="modal-option"
            onClick={() => handleModeSelect('send')}
          >
            <div className="modal-option-title">Send Encrypted</div>
            <div className="modal-option-desc">Send securely to a recipient</div>
          </button>
          <button
            className="modal-option"
            onClick={() => handleModeSelect('store')}
          >
            <div className="modal-option-title">Store File</div>
            <div className="modal-option-desc">Save encrypted for yourself</div>
          </button>
          <button
            className="btn btn-text"
            onClick={() => setShowModeModal(false)}
            style={{ marginTop: 16, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            Cancel
          </button>
        </Modal>
      </div>
    );
  }

  // Visitor landing page (unchanged)
  return (
    <div
      className="dropzone-wrapper"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Layout theme="coral">
        <div className="content-centered dropzone">
          <h1 className="heading-xl">
            An easy and secure way to send your files.
          </h1>
          <p className="text-secondary" style={{ marginBottom: 40 }}>
            No central server. No tracking. No backdoors.
          </p>

          <div
            className="file-action text-action"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label="Select a file to upload"
          >
            <SparkleIcon className="file-action-icon" />
            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
              select
            </span>
            <span style={{ margin: '0 8px' }}>or</span>
            <DropArrowIcon className="file-action-icon" />
            <span>drop a file</span>
          </div>

          {multiFileWarning && (
            <p style={{ marginTop: 16, fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
              Multiple files detected. Only the first file will be uploaded.
            </p>
          )}

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
        </div>
      </Layout>

      {isDragging && (
        <div className="drop-indicator">Drop your file here</div>
      )}

      {/* Mode Selection Modal */}
      <Modal
        isOpen={showModeModal}
        onClose={() => setShowModeModal(false)}
        title={`What would you like to do with ${selectedFile?.name}?`}
      >
        <button
          className="modal-option"
          onClick={() => handleModeSelect('quick')}
        >
          <div className="modal-option-title">Quick Share</div>
          <div className="modal-option-desc">Unencrypted, no login needed</div>
        </button>
        <button
          className="modal-option"
          onClick={() => handleModeSelect('send')}
        >
          <div className="modal-option-title">Send Encrypted</div>
          <div className="modal-option-desc">
            Send securely to a recipient
            {!isLoggedIn && <span style={{ opacity: 0.7 }}> (login required)</span>}
          </div>
        </button>
        <button
          className="modal-option"
          onClick={() => handleModeSelect('store')}
        >
          <div className="modal-option-title">Store File</div>
          <div className="modal-option-desc">
            Save encrypted for yourself
            {!isLoggedIn && <span style={{ opacity: 0.7 }}> (login required)</span>}
          </div>
        </button>
        <button
          className="btn btn-text"
          onClick={() => setShowModeModal(false)}
          style={{ marginTop: 16, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        >
          Cancel
        </button>
      </Modal>
    </div>
  );
}
